// src/admin/screens/audit/AuditLogScreen.tsx
// Read-only console of every audited admin action. Filters are URL-bound so
// a super-admin can deep-link "every loan.approve in April" to a colleague.
// RLS on admin_audit_log enforces who sees what (collections agents see their
// own; admins/super-admins see everything).
//
// Visual: SectionHead with description + Export button, then a Card holding
// the four filter inputs, then the DataTable. Each row's "View" button opens
// the full payload in a Modal.
//
// CSV util is two-step: rowsToCsv(rows, columns) → string, then
// downloadCsv(filename, csv). Actions list derives from the AuditAction type
// exported by @admin/lib/audit so the dropdown stays in sync with what
// recordAudit can ever insert.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import DataTable, { type Column } from '@admin/components/DataTable';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import Modal from '@admin/components/Modal';
import FormField from '@admin/components/FormField';
import SectionHead from '@admin/components/SectionHead';
import Avatar from '@admin/components/Avatar';
import { useAuditLog, type AuditRow, type AuditFilters } from '@admin/hooks/useAuditLog';
import { formatCatDateTime } from '@admin/utils/dates';
import { rowsToCsv, downloadCsv } from '@admin/utils/csv';
import type { AuditAction } from '@admin/lib/audit';
import styles from './AuditLogScreen.module.css';

const ACTIONS = [
  'loan.approve', 'loan.cancel', 'loan.mark_disbursed', 'loan.record_payment',
  'loan.request_restructure', 'loan.recommend_default', 'loan.force_fail_disbursement',
  'loan.reopen_cancelled', 'loan.assign_collections',
  'borrower.freeze', 'borrower.unfreeze', 'borrower.reset_phone', 'borrower.add_note',
  'kyc.approve', 'kyc.reject', 'kyc.request_resubmit', 'kyc.view_media',
  'collection.log_contact',
  'admin.invite', 'admin.deactivate', 'admin.reset_totp', 'admin.set_readonly', 'admin.change_role',
  'config.update',
  'action.review',
  'search.global', 'export.csv',
] as const satisfies readonly AuditAction[];

function urlOrUndef(params: URLSearchParams, key: string): string | undefined {
  const v = params.get(key);
  return v && v.length > 0 ? v : undefined;
}

export default function AuditLogScreen() {
  const [params, setParams] = useSearchParams();
  const filters: AuditFilters = useMemo(() => {
    const f: AuditFilters = {};
    const action = urlOrUndef(params, 'action');
    const target_type = urlOrUndef(params, 'target_type');
    const from = urlOrUndef(params, 'from');
    const to = urlOrUndef(params, 'to');
    if (action) f.action = action;
    if (target_type) f.target_type = target_type;
    if (from) f.from = from;
    if (to) f.to = to;
    return f;
  }, [params]);
  const { data, isLoading, error } = useAuditLog(filters);
  const [drawer, setDrawer] = useState<AuditRow | null>(null);
  const rowCount = data?.length ?? 0;

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value.length === 0) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const columns = useMemo<Column<AuditRow>[]>(
    () => [
      {
        key: 'when',
        header: 'When',
        render: (r) => (
          <span className="mono" style={{ fontSize: 12.5, color: 'var(--c-text-muted)' }}>
            {formatCatDateTime(r.created_at)}
          </span>
        ),
        width: '180px',
      },
      {
        key: 'admin',
        header: 'Admin',
        render: (r) => {
          const name = r.admin_user?.display_name ?? r.admin_user?.email ?? 'system';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar name={name} size={26} />
              <span style={{ fontWeight: 500 }}>{name}</span>
            </div>
          );
        },
      },
      {
        key: 'action',
        header: 'Action',
        render: (r) => <code className={styles.code}>{r.action}</code>,
      },
      {
        key: 'target',
        header: 'Target',
        render: (r) =>
          r.target_type
            ? <span className="mono" style={{ fontSize: 12.5 }}>{r.target_type}{r.target_id ? `:${r.target_id.slice(0, 8)}` : ''}</span>
            : <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
      },
      {
        key: 'view',
        header: '',
        width: '80px',
        align: 'right',
        render: (r) => (
          <button
            type="button"
            className={styles.linkBtn}
            onClick={(e) => {
              e.stopPropagation();
              setDrawer(r);
            }}
          >
            View
          </button>
        ),
      },
    ],
    [],
  );

  const onExport = () => {
    if (!data || data.length === 0) return;
    const flat = data.map((r) => ({
      created_at: r.created_at,
      admin_name: r.admin_user?.display_name ?? '',
      admin_email: r.admin_user?.email ?? '',
      action: r.action,
      target_type: r.target_type ?? '',
      target_id: r.target_id ?? '',
      payload: r.payload ? JSON.stringify(r.payload) : '',
    }));
    const csv = rowsToCsv(flat, [
      { key: 'created_at', header: 'When (UTC)' },
      { key: 'admin_name', header: 'Admin' },
      { key: 'admin_email', header: 'Admin email' },
      { key: 'action', header: 'Action' },
      { key: 'target_type', header: 'Target type' },
      { key: 'target_id', header: 'Target id' },
      { key: 'payload', header: 'Payload' },
    ]);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`audit-log-${today}.csv`, csv);
  };

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Compliance"
        title="Audit log"
        description={`${rowCount} entr${rowCount === 1 ? 'y' : 'ies'} · immutable record of every admin and system action`}
        action={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download size={14} />}
            onClick={onExport}
            disabled={!data || data.length === 0}
          >
            Export CSV
          </Button>
        }
      />

      <Card title="Filters">
        <div className={styles.filters}>
          <FormField label="Action" htmlFor="action">
            <select
              id="action"
              value={filters.action ?? ''}
              onChange={(e) => setParam('action', e.target.value)}
            >
              <option value="">All actions</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Target type" htmlFor="tt" hint="loan, borrower, kyc, admin…">
            <input
              id="tt"
              placeholder="loan / kyc / admin…"
              value={filters.target_type ?? ''}
              onChange={(e) => setParam('target_type', e.target.value)}
            />
          </FormField>
          <FormField label="From" htmlFor="from">
            <input
              id="from"
              type="date"
              value={filters.from ?? ''}
              onChange={(e) => setParam('from', e.target.value)}
            />
          </FormField>
          <FormField label="To" htmlFor="to">
            <input
              id="to"
              type="date"
              value={filters.to ?? ''}
              onChange={(e) => setParam('to', e.target.value)}
            />
          </FormField>
        </div>
      </Card>

      <DataTable
        rows={data}
        columns={columns}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        rowKey={(r) => r.id}
        emptyLabel="No audit rows match those filters."
      />

      {drawer && (
        <Modal
          open
          onClose={() => setDrawer(null)}
          title={
            <span>
              Payload — <code className={styles.code}>{drawer.action}</code>
            </span>
          }
          width={640}
        >
          <p className={styles.drawerMeta}>
            {formatCatDateTime(drawer.created_at)} ·{' '}
            {drawer.admin_user?.display_name ?? drawer.admin_user?.email ?? '—'}
            {drawer.target_type && (
              <>
                {' '}· target <code className={styles.code}>
                  {drawer.target_type}
                  {drawer.target_id ? `:${drawer.target_id}` : ''}
                </code>
              </>
            )}
          </p>
          <pre className={styles.json}>
            {JSON.stringify(drawer.payload ?? {}, null, 2)}
          </pre>
        </Modal>
      )}
    </div>
  );
}
